// ============================================================================
// DOCUMENT TASK SERVICE - SERVICIO PARA GESTIÓN DE VINCULACIONES DOCUMENTO-TAREA
// ============================================================================

import apiClient from './apiClient';

/**
 * Servicio específico para manejar las vinculaciones entre documentos y tareas
 */
class DocumentTaskService {
  
  /**
   * Realizar petición HTTP con manejo de errores y autenticación
   */
  async makeRequest(endpoint, options = {}, token = null) {
    try {
      // El apiClient deriva la base URL por tenant, pone el Bearer y lanza en !ok.
      const data = await apiClient.request(endpoint, { ...options, token });

      // Preservar el comportamiento histórico: un envelope 200 con status:error
      // también se trata como fallo.
      if (data && data.status === 'error') {
        throw new Error(data.message || 'Error desconocido del servidor');
      }

      return data;
    } catch (error) {
      console.error('❌ DocumentTask API Error:', error);
      throw error;
    }
  }

  /**
   * Obtener documentos vinculados a una tarea específica
   */
  async getDocumentsByTask(taskId, token = null) {
    try {
      console.log('📋 Getting documents for task:', taskId);
      const response = await this.makeRequest(`documentos_tarea&task_id=${taskId}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting documents by task:', error);
      throw error;
    }
  }

  /**
   * Obtener tareas vinculadas a un documento específico
   */
  async getTasksByDocument(documentId, token = null) {
    try {
      console.log('📄 Getting tasks for document:', documentId);
      const response = await this.makeRequest(`tarea_documentos&documento_id=${documentId}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting tasks by document:', error);
      throw error;
    }
  }

  /**
   * Vincular un documento a una tarea
   */
  async linkDocumentToTask(documentId, taskId, vinculadoPor = 1, token = null) {
    try {
      console.log('🔗 Linking document to task:', { documentId, taskId, vinculadoPor });
      
      // Validar antes de vincular
      await this.validateDocumentTaskLink(documentId, taskId, token);
      
      const response = await this.makeRequest('link_documento_tarea', {
        method: 'POST',
        body: JSON.stringify({
          documento_id: documentId,
          tarea_id: taskId,
          vinculado_por: vinculadoPor
        })
      }, token);
      
      console.log('✅ Document linked successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error linking document to task:', error);
      throw error;
    }
  }

  /**
   * Desvincular un documento de una tarea
   */
  async unlinkDocumentFromTask(documentId, taskId, token = null) {
    try {
      console.log('🔓 Unlinking document from task:', { documentId, taskId });
      
      const response = await this.makeRequest(
        `unlink_documento_tarea&documento_id=${documentId}&tarea_id=${taskId}`, 
        { method: 'DELETE' },
        token
      );
      
      console.log('✅ Document unlinked successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error unlinking document from task:', error);
      throw error;
    }
  }

  /**
   * Verificar si un documento ya está vinculado a una tarea
   */
  async checkDocumentTaskLink(documentId, taskId, token = null) {
    try {
      console.log('🔍 Checking document-task link:', { documentId, taskId });
      
      const response = await this.makeRequest(
        `check_documento_tarea&documento_id=${documentId}&tarea_id=${taskId}`,
        {},
        token
      );
      
      return response.data;
    } catch (error) {
      console.error('❌ Error checking document-task link:', error);
      return { isLinked: false };
    }
  }

  /**
   * Obtener todas las vinculaciones de un usuario
   */
  async getUserDocumentTaskLinks(userId, token = null) {
    try {
      console.log('👤 Getting user document-task links:', userId);
      
      const response = await this.makeRequest(`user_documento_tarea_links&user_id=${userId}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting user document-task links:', error);
      throw error;
    }
  }

  /**
   * Validar vinculación antes de crear
   */
  async validateDocumentTaskLink(documentId, taskId, token = null) {
    try {
      console.log('✅ Validating document-task link:', { documentId, taskId });
      
      // Verificar que el documento existe (con token: sin él el backend devuelve 401
      // por la puerta central de auth y la vinculación fallaba siempre)
      const document = await this.getDocument(documentId, token);
      if (!document) {
        throw new Error('Documento no encontrado');
      }

      // Verificar que no existe ya la vinculación
      const linkCheck = await this.checkDocumentTaskLink(documentId, taskId, token);
      if (linkCheck && linkCheck.isLinked) {
        throw new Error('El documento ya está vinculado a esta tarea');
      }

      return true;
    } catch (error) {
      console.error('❌ Validation failed:', error);
      throw error;
    }
  }

  /**
   * Obtener documento por ID (método auxiliar)
   */
  async getDocument(documentId, token = null) {
    try {
      const response = await this.makeRequest(`documento&id=${documentId}`, {}, token);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting document:', error);
      return null;
    }
  }

  /**
   * Obtener estadísticas de vinculaciones
   */
  async getDocumentTaskStats(token = null) {
    try {
      console.log('📊 Getting document-task statistics');
      const response = await this.makeRequest('document_task_stats', {}, token);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting document-task stats:', error);
      throw error;
    }
  }

  /**
   * Buscar documentos vinculados con filtros
   */
  async searchLinkedDocuments(filters = {}, token = null) {
    try {
      console.log('🔍 Searching linked documents with filters:', filters);
      
      const queryParams = new URLSearchParams();
      
      if (filters.taskId) queryParams.append('task_id', filters.taskId);
      if (filters.documentType) queryParams.append('document_type', filters.documentType);
      if (filters.userId) queryParams.append('user_id', filters.userId);
      if (filters.dateFrom) queryParams.append('date_from', filters.dateFrom);
      if (filters.dateTo) queryParams.append('date_to', filters.dateTo);
      
      const endpoint = `search_linked_documents&${queryParams.toString()}`;
      const response = await this.makeRequest(endpoint, {}, token);
      
      return response.data || [];
    } catch (error) {
      console.error('❌ Error searching linked documents:', error);
      throw error;
    }
  }

  /**
   * Vincular múltiples documentos a una tarea
   */
  async linkMultipleDocumentsToTask(documentIds, taskId, vinculadoPor = 1, token = null) {
    try {
      console.log('🔗 Linking multiple documents to task:', { documentIds, taskId });
      
      const results = [];
      
      for (const documentId of documentIds) {
        try {
          const result = await this.linkDocumentToTask(documentId, taskId, vinculadoPor, token);
          results.push({ documentId, success: true, data: result });
        } catch (error) {
          console.error(`❌ Error linking document ${documentId}:`, error);
          results.push({ documentId, success: false, error: error.message });
        }
      }
      
      return results;
    } catch (error) {
      console.error('❌ Error in bulk linking:', error);
      throw error;
    }
  }

  /**
   * Desvincular múltiples documentos de una tarea
   */
  async unlinkMultipleDocumentsFromTask(documentIds, taskId, token = null) {
    try {
      console.log('🔓 Unlinking multiple documents from task:', { documentIds, taskId });
      
      const results = [];
      
      for (const documentId of documentIds) {
        try {
          const result = await this.unlinkDocumentFromTask(documentId, taskId, token);
          results.push({ documentId, success: true, data: result });
        } catch (error) {
          console.error(`❌ Error unlinking document ${documentId}:`, error);
          results.push({ documentId, success: false, error: error.message });
        }
      }
      
      return results;
    } catch (error) {
      console.error('❌ Error in bulk unlinking:', error);
      throw error;
    }
  }
}

// Exportar una instancia singleton
const documentTaskService = new DocumentTaskService();
export default documentTaskService;